import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AddFieldInput,
  CreateTemplateInput,
  MeasurementTemplate,
  UpdateFieldInput,
  UpdateTemplateInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  addField,
  createTemplate,
  deleteField,
  listTemplates,
  reorderFields,
  updateField,
  updateTemplate,
  type TemplateList,
} from './templates-api.js';

/**
 * The template editor's data layer.
 *
 * Every mutation writes the returned template straight back into the list cache rather than
 * invalidating it. Reordering fires once per drag, and a refetch between each one is how a
 * list ends up briefly showing the order the shop just moved away from.
 */
export const templateKeys = {
  all: ['measurement-templates'] as const,
  list: (includeInactive: boolean) => ['measurement-templates', 'list', includeInactive] as const,
};

export function useTemplates(
  options: { includeInactive?: boolean; enabled?: boolean } = {},
): UseQueryResult<TemplateList> {
  const client = useApiClient();
  const includeInactive = options.includeInactive ?? false;
  return useQuery({
    queryKey: templateKeys.list(includeInactive),
    queryFn: () => listTemplates(client, { includeInactive }),
    // `enabled` is for screens that refuse a role outright — the garments page shows a price on
    // every row, so a karigar is turned away and there is nothing to fetch. It is NOT a permission
    // check: garment types are readable by a tailor, who needs them to take measurements.
    enabled: options.enabled ?? true,
  });
}

/** Replace one template wherever it appears in the cache, leaving its neighbours alone. */
function useWriteBack() {
  const queryClient = useQueryClient();
  return (template: MeasurementTemplate): void => {
    queryClient.setQueriesData<TemplateList>(
      { queryKey: templateKeys.all },
      (previous) =>
        previous && {
          ...previous,
          templates: previous.templates.map((one) =>
            one.garmentTypeKey === template.garmentTypeKey ? template : one,
          ),
        },
    );
  };
}

export function useCreateTemplate() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MeasurementTemplate, unknown, CreateTemplateInput>({
    mutationFn: (input) => createTemplate(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function useUpdateTemplate(garmentTypeKey: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MeasurementTemplate, unknown, UpdateTemplateInput>({
    mutationFn: (patch) => updateTemplate(client, garmentTypeKey, patch),
    // Hiding a garment type changes which templates the list contains, so this one refetches.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function useAddField(garmentTypeKey: string) {
  const client = useApiClient();
  const writeBack = useWriteBack();
  return useMutation<MeasurementTemplate, unknown, AddFieldInput>({
    mutationFn: (input) => addField(client, garmentTypeKey, input),
    onSuccess: writeBack,
  });
}

export function useUpdateField(garmentTypeKey: string) {
  const client = useApiClient();
  const writeBack = useWriteBack();
  return useMutation<MeasurementTemplate, unknown, { fieldKey: string; patch: UpdateFieldInput }>({
    mutationFn: ({ fieldKey, patch }) => updateField(client, garmentTypeKey, fieldKey, patch),
    onSuccess: writeBack,
  });
}

export function useReorderFields(garmentTypeKey: string) {
  const client = useApiClient();
  const writeBack = useWriteBack();
  return useMutation<MeasurementTemplate, unknown, string[]>({
    mutationFn: (order) => reorderFields(client, garmentTypeKey, order),
    onSuccess: writeBack,
  });
}

export function useDeleteField(garmentTypeKey: string) {
  const client = useApiClient();
  const writeBack = useWriteBack();
  return useMutation<MeasurementTemplate, unknown, string>({
    mutationFn: (fieldKey) => deleteField(client, garmentTypeKey, fieldKey),
    onSuccess: writeBack,
  });
}
